export const dynamic = 'force-dynamic';
import { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import { sortAssets } from '@/_pages/interwill/editing/components/EditingContent/components/AssetsBlock/helpers';
import { AssetCategoryToText } from '@/entities/assets';
import { useIwEditingStore } from '@/entities/interwills';
import { COLORS } from '@/shared/constants/colors';
import { getCountryName } from '@/shared/constants/countries-list';
import { getUppercaseName } from '@/shared/helpers/names';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { DateTimeIso } from '@/shared/types';
import { dates } from '@/shared/utils/dates';

import { useGetDocumentCategory, useWithPlaceholder } from './hooks/';
import { DisplayAsset } from './DisplayAsset';
import { DisplayBeneficiary } from './DisplayBeneficiary';
import { getResidenceAddress, initFonts, LANGUAGE_TO_FONT_FAMILY } from './helpers';

import { styles } from './styles';

type LastWillPDFProps = {
  isDraft: boolean;
  executionPlace?: string;
  date?: DateTimeIso;
  id?: string;
};

initFonts();

export const LastWillPDF: FC<LastWillPDFProps> = ({ isDraft, date, id, executionPlace }) => {
  const {
    introductionMessage,
    testator,
    witnesses,
    beneficiaries,
    contingentBeneficiary,
    disinheritedPerson,
    assets,
    spouse,
    distribution,
    executor,
    subExecutor,
    shariaLaws,
  } = useIwEditingStore.getState(); // https://github.com/diegomura/react-pdf/issues/2263
  const { t, i18n } = useTranslation('widgets/pdfs', {
    keyPrefix: 'lastWill',
  });
  const { t: ta } = useTranslation(['assets', 'forms_assets']);

  const chosenFont =
    LANGUAGE_TO_FONT_FAMILY[(i18n.language ?? 'EN').toLowerCase()] ?? LANGUAGE_TO_FONT_FAMILY.latin;

  const testatorFullName = getUppercaseName(testator);

  const withPlaceholder = useWithPlaceholder();
  const getDocumentCategory = useGetDocumentCategory();

  const itemCounters: Record<string, number> = {
    testator: 1,
    distribution: 2,
    executor: 1,
    subExecutor: 1,
    others: 5,
    attestation: 0,
  };

  const sortedAssets = useMemo(() => {
    return sortAssets(assets);
  }, [assets]);

  const assetsWithCategoryIndex = useMemo(() => {
    const categoryIndex: Record<string, number> = {};
    return sortedAssets.map((asset) => {
      categoryIndex[asset.category] = (categoryIndex[asset.category] || 0) + 1;
      return { ...asset, displayIndex: categoryIndex[asset.category] };
    });
  }, [sortedAssets]);

  const beneficiaryMap = useMemo(
    () => new Map(beneficiaries.map((b, i) => [b.id, { ...b, index: i + 1 }])),
    [beneficiaries],
  );

  const getTableBeneficiaryName = (beneficiaryId: DtoId | undefined) => {
    if (!beneficiaryId) return undefined;
    const ben = beneficiaryMap.get(beneficiaryId);
    if (!ben) return undefined;

    return t('distributionBlock.benName', {
      firstName: ben.firstName,
      index: ben.index,
    });
  };

  const additionalMessageHeight = useMemo(() => {
    // othervise text overlaps
    return (introductionMessage?.match(/\n/g)?.length ?? 0) * 1.2;
  }, [introductionMessage]);

  return (
    <Document style={{ fontFamily: chosenFont }}>
      <Page size={{ width: 794, height: 1056 }} style={styles.page} break={false} wrap={false}>
        <View style={styles.border} />

        <View style={styles.header}>
          {date ? (
            <Text>{dates.formatLong(date)}</Text>
          ) : (
            <Text style={styles.placeholder}>{t('placeholder.date')}</Text>
          )}

          <Text>
            {t('lastWillOf')} {withPlaceholder(testatorFullName, 'testatorFullName')}
          </Text>
          <Text>
            {t('number')}: <Text>{withPlaceholder(id, 'willNumber')}</Text>
          </Text>
        </View>

        <View style={{ ...styles.section, height: '100%' }}>
          <View style={{ ...styles.main, ...styles.center }}>
            <Text style={styles.title}> {t('title')}</Text>
            {i18n.language === 'en' && (
              <Text style={{ fontSize: 29, textAlign: 'center' }}>of</Text>
            )}
            <Text style={{ fontSize: 28, marginTop: 10, textAlign: 'center' }}>
              {' '}
              {withPlaceholder(testatorFullName, 'testatorFullName')}
            </Text>
          </View>
        </View>

        <View style={{ ...styles.footer, ...styles.footerFirst }} fixed>
          <View fixed style={styles.poweredByText}>
            <Text>{t('poweredBy')}</Text>
          </View>
        </View>
      </Page>

      <Page size={{ width: 794, height: 1056 }} style={styles.page}>
        <View fixed style={styles.border} />
        {isDraft && (
          <View fixed style={{ ...styles.watermark }}>
            <Text>DRAFT OF LAST WILL AND TESTAMENT</Text>
            <Text>by Owner.One</Text>
          </View>
        )}
        <View style={styles.header} fixed>
          {date ? (
            <Text>{dates.formatLong(date)}</Text>
          ) : (
            <Text style={styles.placeholder}>{t('placeholder.date')}</Text>
          )}

          <Text>
            {t('lastWillOf')} {withPlaceholder(testatorFullName, 'testatorFullName')}
          </Text>
          <Text>
            {t('number')}: <Text>{withPlaceholder(id, 'willNumber')}</Text>
          </Text>
        </View>

        <View style={styles.section}>
          <View
            style={{
              ...styles.messageWrapper,
            }}
          >
            {introductionMessage && (
              <>
                <Text style={{ marginBottom: additionalMessageHeight }}>{introductionMessage}</Text>
                <Text style={{ textTransform: 'capitalize' }}>
                  {withPlaceholder(testatorFullName.toLowerCase(), 'testatorFullName')}
                </Text>
              </>
            )}
            <Text style={{ marginTop: 12, fontStyle: 'normal', fontSize: 14 }}>
              {t('testatorBlock.description')}
            </Text>
          </View>
          <View style={{ ...styles.main, ...styles.mainPadding }}>
            <Text style={styles.blockTitle}>{t('testatorBlock.title')}</Text>
            <Text>
              {t('testatorBlock.i')} {withPlaceholder(testatorFullName, 'testatorFullName')},{' '}
              {t('testatorBlock.dateOfBirth')}{' '}
              {withPlaceholder(testator.birthDate && dates.formatLong(testator.birthDate), 'date')},{' '}
              {t('testatorBlock.gender')}{' '}
              {withPlaceholder(
                testator.gender ? t(`genders.${testator.gender}`) : undefined,
                'gender',
              )}
              .
            </Text>

            <View style={styles.row} wrap={false}>
              <View style={styles.listNumber}>
                <Text>1.{itemCounters.testator++}</Text>
              </View>
              <View style={styles.testatorInfoRow}>
                <Text>
                  {t('citizenship.primary')}{' '}
                  {withPlaceholder(getCountryName(testator.primaryCitizenship?.country), 'country')}
                  ,{' '}
                  {withPlaceholder(
                    getDocumentCategory(testator.primaryCitizenship?.documentType),
                    'docType',
                  )}
                  : {t('citizenship.number')}{' '}
                  {withPlaceholder(testator.primaryCitizenship?.number, 'number')},{' '}
                  {t('citizenship.dateOfIssue')}{' '}
                  {withPlaceholder(
                    testator.primaryCitizenship?.issueDate &&
                      dates.formatLong(testator.primaryCitizenship?.issueDate),
                    'date',
                  )}
                  .
                </Text>
              </View>
            </View>

            {testator.otherCitizenships?.length && (
              <View style={styles.row} wrap={false}>
                <View style={styles.listNumber}>
                  <Text>1.{itemCounters.testator++}</Text>
                </View>
                <View style={styles.testatorInfoRow}>
                  <Text>
                    {t('citizenship.otherAndPermits')}:{' '}
                    {testator.otherCitizenships?.map((citzenship, i, arr) => (
                      <View key={i}>
                        <Text>
                          {withPlaceholder(getCountryName(citzenship?.country), 'country')},{' '}
                          {withPlaceholder(
                            getDocumentCategory(citzenship?.documentType),
                            'docType',
                          )}
                          : {t('citizenship.number')}{' '}
                          {withPlaceholder(citzenship?.number, 'number')},{' '}
                          {t('citizenship.dateOfIssue')}{' '}
                          {withPlaceholder(
                            citzenship?.issueDate && dates.formatLong(citzenship?.issueDate),
                            'date',
                          )}
                          {i === arr.length - 1 ? '.' : '; '}
                        </Text>
                      </View>
                    ))}
                  </Text>
                </View>
              </View>
            )}

            <View style={styles.row} wrap={false}>
              <View style={styles.listNumber}>
                <Text>1.{itemCounters.testator++}</Text>
              </View>
              <View style={styles.testatorInfoRow}>
                <Text>
                  {t('residence.primary')}:{' '}
                  {withPlaceholder(getResidenceAddress(testator.primaryResidence), 'address')}
                  {testator.primaryResidence?.tax && (
                    <>
                      , {t('residence.taxId')}: {testator.primaryResidence?.tax}
                    </>
                  )}
                  .
                </Text>
              </View>
            </View>

            {testator.otherResidences?.length && (
              <View style={styles.row} wrap={false}>
                <View style={styles.listNumber}>
                  <Text>1.{itemCounters.testator++}</Text>
                </View>
                <View style={styles.testatorInfoRow}>
                  <Text>
                    {t('residence.additional')}:{' '}
                    {testator.otherResidences?.map((residence, i, arr) => (
                      <View key={i}>
                        <Text>
                          {withPlaceholder(getResidenceAddress(residence), 'address')}
                          {residence?.tax && (
                            <>
                              , {t('residence.taxId')}: {residence?.tax}
                            </>
                          )}
                          {i === arr.length - 1 ? '.' : '; '}
                        </Text>
                      </View>
                    ))}{' '}
                  </Text>
                </View>
              </View>
            )}

            <View>
              <Text>{t('testatorBlock.madeTheWill')}</Text>
            </View>

            <Text style={styles.blockTitle}>{t('witnessBlock.title')}</Text>
            <Text>{t('witnessBlock.description')}</Text>

            {witnesses.map((witness, i) => {
              return (
                <DisplayBeneficiary
                  block="witnessBlock"
                  beneficiary={witness}
                  key={i}
                  listNumber={`2.${i + 1}`}
                  index={i}
                />
              );
            })}

            <Text style={styles.blockTitle}>{t('beneficiariesBlock.title')}</Text>
            <Text>{t('beneficiariesBlock.description')}</Text>

            {beneficiaries.map((beneficiary, i) => {
              return (
                <DisplayBeneficiary
                  block="beneficiariesBlock"
                  beneficiary={beneficiary}
                  key={i}
                  listNumber={`3.${i + 1}`}
                  index={i}
                />
              );
            })}

            {contingentBeneficiary && (
              <DisplayBeneficiary
                block="contingentBeneficiariesBlockBlock"
                beneficiary={contingentBeneficiary}
                listNumber={`3.${beneficiaries.length + 1}`}
                index={0}
              />
            )}

            {disinheritedPerson.map((person, i) => (
              <View key={i} style={styles.beneficiaryBlock}>
                <View style={styles.row} wrap={false}>
                  <View style={styles.listNumber}>
                    <Text>3.{beneficiaries.length + (contingentBeneficiary ? 2 : 1) + i}</Text>
                  </View>
                  <Text style={styles.subTitle}>
                    {t('disinheritedPerson')} {i + 1}
                  </Text>
                </View>

                <View style={{ ...styles.row, paddingLeft: 36 }}>
                  <Text>
                    {withPlaceholder(getUppercaseName(person), 'fullName')},{' '}
                    {t('witnessBlock.dateOfBirth')}{' '}
                    {withPlaceholder(
                      person.birthDate && dates.formatLong(person.birthDate),
                      'date',
                    )}
                    , {t('witnessBlock.gender')}{' '}
                    {withPlaceholder(
                      person.gender ? t(`genders.${person.gender}`) : undefined,
                      'gender',
                    )}
                    ,{' '}
                  </Text>
                </View>

                <View style={{ paddingLeft: 36 }}>
                  <Text>
                    {t('beneficiariesBlock.refferedAsDisinherited', {
                      fullName: person.firstName,
                      index: i + 1,
                    })}
                  </Text>
                </View>
              </View>
            ))}

            <Text style={styles.blockTitle}>{t('assetsBlock.title')}</Text>
            <View style={styles.row} wrap={false}>
              <View style={styles.listNumber}>
                <Text>4.1</Text>
              </View>
              <Text>{t('assetsBlock.description')}</Text>
            </View>

            {sortedAssets.map((asset, i) => (
              <DisplayAsset
                itemCounters={itemCounters}
                asset={asset}
                testator={testator}
                index={i + 1}
                key={asset.id}
                isDraft={isDraft}
              />
            ))}

            <Text style={styles.blockTitle}>{t('distributionBlock.title')}</Text>
            <View style={styles.row} wrap={false}>
              <View style={styles.listNumber}>
                <Text>5.1</Text>
              </View>
              <Text>{t('distributionBlock.text1')}</Text>
            </View>
            <View style={styles.row} wrap={false}>
              <View style={styles.listNumber}>
                <Text>5.2</Text>
              </View>
              <View style={{ width: 570 }}>
                <Text>{t('distributionBlock.text2')}</Text>
              </View>
            </View>
            <View style={styles.row} wrap={false}>
              <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                <Text>5.2.1</Text>
              </View>
              <View style={{ width: 533 }}>
                <Text>{t('distributionBlock.text21')}</Text>
              </View>
            </View>

            <View style={styles.table}>
              <View style={{ margin: '0  0 12px 24px' }}>
                <Text>{t('distributionBlock.table')}</Text>
              </View>
              <View style={{ ...styles.tableRow, height: 92 }} wrap={false}>
                <View style={styles.tableCell}>
                  <Text>{t('distributionBlock.asset')}</Text>
                </View>
                <View style={{ ...styles.tableCell, ...styles.tableCellWide }}>
                  <Text>{t('distributionBlock.primaryBen')}</Text>
                </View>
                <View style={{ ...styles.tableCell, ...styles.tableCellWide }}>
                  <Text>{t('distributionBlock.subBen1')}</Text>
                </View>
                <View style={{ ...styles.tableCell, ...styles.tableCellWide }}>
                  <Text>{t('distributionBlock.subBen2')}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text>{t('distributionBlock.sharing')}</Text>
                </View>
              </View>

              {assetsWithCategoryIndex.map((asset, assetIdx) => {
                return (
                  <View
                    key={asset.id}
                    style={{
                      ...styles.tableRow,
                      height: distribution[asset.id].length * 64,
                    }}
                    wrap={false}
                  >
                    <View
                      style={{
                        ...styles.tableCell,
                        backgroundColor: assetIdx % 2 === 0 ? '#f1f1f1' : COLORS.white,
                      }}
                    >
                      <Text>
                        {ta(AssetCategoryToText[asset.category])} {asset.displayIndex}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      {distribution[asset.id]?.map((distItem, i) => {
                        return (
                          <View
                            key={i}
                            style={{
                              ...styles.tableRow,
                              height: distribution[asset.id].length * 84,
                              backgroundColor: assetIdx % 2 === 0 ? '#f1f1f1' : COLORS.white,
                            }}
                          >
                            <View style={{ ...styles.tableCell, ...styles.tableCellWide }}>
                              <Text>
                                {withPlaceholder(
                                  getTableBeneficiaryName(distItem.primaryBeneficiary),
                                  'benName',
                                )}
                              </Text>
                            </View>
                            <View style={{ ...styles.tableCell, ...styles.tableCellWide }}>
                              <Text>
                                {getTableBeneficiaryName(distItem.firstSubstitute) ??
                                  t('placeholder.no')}
                              </Text>
                            </View>
                            <View style={{ ...styles.tableCell, ...styles.tableCellWide }}>
                              <Text>
                                {getTableBeneficiaryName(distItem.secondSubstitute) ??
                                  t('placeholder.no')}
                              </Text>
                            </View>
                            <View style={styles.tableCell}>
                              <Text>{distItem.percentage}%</Text>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                );
              })}
            </View>

            {contingentBeneficiary && (
              <View style={styles.row} wrap={false}>
                <View style={styles.listNumber}>
                  <Text>5.{++itemCounters.distribution}</Text>
                </View>
                <View style={{ paddingRight: 36 }}>
                  <Text>{t('distributionBlock.text3')}</Text>
                </View>
              </View>
            )}

            {disinheritedPerson.length > 0 && (
              <View style={styles.beneficiaryBlock}>
                <View style={styles.row} wrap={false}>
                  <View style={styles.listNumber}>
                    <Text>5.{++itemCounters.distribution}</Text>
                  </View>
                  <View style={{ paddingRight: 36 }}>
                    <Text>{t('distributionBlock.excludeTitle')}</Text>
                  </View>
                </View>
                <View style={{ paddingLeft: 36 }}>
                  {disinheritedPerson.map((person, i) => (
                    <View key={i} style={{ paddingTop: 8 }}>
                      <Text>
                        {t('distributionBlock.disinheritedPersonN', {
                          firstName: person.firstName?.toUpperCase(),
                          index: i + 1,
                        })}
                        {disinheritedPerson.length - 1 === i ? '.' : ','}
                      </Text>
                    </View>
                  ))}
                </View>
                <View style={styles.row}>
                  <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }} wrap={false}>
                    <Text>5.{itemCounters.distribution}.1</Text>
                  </View>
                  <View style={{ paddingRight: 72 }}>
                    <Text>{t('distributionBlock.executionDescription')}</Text>
                  </View>
                </View>
              </View>
            )}

            {spouse && (
              <View style={styles.row} wrap={false}>
                <View style={styles.listNumber}>
                  <Text>5.{++itemCounters.distribution}</Text>
                </View>
                <View style={{ paddingRight: 36 }}>
                  <Text>
                    {spouse === contingentBeneficiary?.id
                      ? t('distributionBlock.spouseTextCont')
                      : t('distributionBlock.spouseText', {
                          index: beneficiaryMap.get(spouse)?.index,
                        })}
                  </Text>
                </View>
              </View>
            )}

            {executor && (
              <View style={styles.beneficiaryBlock}>
                <Text style={styles.blockTitle}>{t('executorBlock.title')}</Text>
                <View style={styles.row} wrap={true}>
                  <View style={styles.listNumber}>
                    <Text>{++itemCounters.others}.1</Text>
                  </View>
                  <View style={{ width: 570 }} wrap={true}>
                    <Text>{t('executorBlock.text1')}</Text>
                  </View>
                </View>
                <View style={styles.row} wrap={false}>
                  <View style={styles.listNumber}>
                    <Text>{itemCounters.others}.2</Text>
                  </View>
                  <Text>{t('executorBlock.appoint')}</Text>
                </View>

                <View break={false} style={{ paddingLeft: 36 }}>
                  <Text>
                    {withPlaceholder(getUppercaseName(executor), 'fullName')},{' '}
                    {t('witnessBlock.dateOfBirth')}{' '}
                    {withPlaceholder(
                      executor.birthDate && dates.formatLong(executor.birthDate),
                      'date',
                    )}
                    , {t('witnessBlock.gender')}{' '}
                    {withPlaceholder(
                      executor.gender ? t(`genders.${executor.gender}`) : undefined,
                      'gender',
                    )}
                    .
                  </Text>
                </View>

                <View style={styles.row} wrap={false}>
                  <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                    <Text>{itemCounters.others}.2.1</Text>
                  </View>
                  <View style={{ ...styles.beneficiaryInfoRow, paddingRight: 72 }}>
                    <Text>
                      {t('citizenship.primary')}{' '}
                      {withPlaceholder(
                        getCountryName(executor.primaryCitizenship?.country),
                        'country',
                      )}
                      ,{' '}
                      {withPlaceholder(
                        getDocumentCategory(executor.primaryCitizenship?.documentType),
                        'docType',
                      )}
                      : {t('citizenship.number')}{' '}
                      {withPlaceholder(executor.primaryCitizenship?.number, 'number')},{' '}
                      {t('citizenship.dateOfIssue')}{' '}
                      {withPlaceholder(
                        executor.primaryCitizenship?.issueDate &&
                          dates.formatLong(executor.primaryCitizenship?.issueDate),
                        'date',
                      )}
                      .
                    </Text>
                  </View>
                </View>

                {executor.otherCitizenships?.length && (
                  <View style={styles.row} wrap={false}>
                    <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                      <Text>
                        {itemCounters.others}.2.{++itemCounters.executor}
                      </Text>
                    </View>
                    <View style={styles.beneficiaryInfoRow}>
                      <Text>
                        {t('citizenship.other')}:{' '}
                        {executor.otherCitizenships?.map((citzenship, i, arr) => (
                          <View key={i}>
                            <Text>
                              {withPlaceholder(getCountryName(citzenship?.country), 'country')},{' '}
                              {withPlaceholder(
                                getDocumentCategory(citzenship?.documentType),
                                'docType',
                              )}
                              : {t('citizenship.number')}{' '}
                              {withPlaceholder(citzenship?.number, 'number')},{' '}
                              {t('citizenship.dateOfIssue')}{' '}
                              {withPlaceholder(
                                citzenship?.issueDate && dates.formatLong(citzenship?.issueDate),
                                'date',
                              )}
                              {i === arr.length - 1 ? '.' : '; '}
                            </Text>
                          </View>
                        ))}
                      </Text>
                    </View>
                  </View>
                )}

                {executor.primaryResidence && (
                  <View style={styles.row} wrap={false}>
                    <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                      <Text>
                        {itemCounters.others}.2.{++itemCounters.executor}
                      </Text>
                    </View>
                    <View style={styles.beneficiaryInfoRow}>
                      <Text>
                        {t('residence.primary')}:{' '}
                        {withPlaceholder(getResidenceAddress(executor.primaryResidence), 'address')}
                        {executor.primaryResidence?.tax && (
                          <>
                            , {t('residence.taxId')}: {executor.primaryResidence?.tax}
                          </>
                        )}
                        .
                      </Text>
                    </View>
                  </View>
                )}

                {executor.otherResidences?.length && (
                  <View style={styles.row} wrap={false}>
                    <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                      <Text>
                        {itemCounters.others}.2.{++itemCounters.executor}
                      </Text>
                    </View>
                    <View style={styles.beneficiaryInfoRow}>
                      <Text>
                        {t('residence.additional')}:{' '}
                        {executor.otherResidences?.map((residence, i, arr) => (
                          <View key={i}>
                            <Text>
                              {withPlaceholder(getResidenceAddress(residence), 'address')}
                              {residence?.tax && (
                                <>
                                  , {t('residence.taxId')}: {residence?.tax}
                                </>
                              )}
                              {i === arr.length - 1 ? '.' : '; '}
                            </Text>
                          </View>
                        ))}{' '}
                      </Text>
                    </View>
                  </View>
                )}

                {(executor.phoneNumber || executor.email) && (
                  <View style={styles.row} wrap={false}>
                    <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                      <Text>
                        {itemCounters.others}.2.{++itemCounters.executor}
                      </Text>
                    </View>
                    <View style={styles.beneficiaryInfoRow}>
                      <Text>
                        {t('contacts.details')}:{' '}
                        {executor.phoneNumber
                          ? `${executor.phoneCode}${executor.phoneNumber}, `
                          : ' '}
                        {executor.email || ''}.
                      </Text>
                    </View>
                  </View>
                )}

                <View style={{ paddingLeft: 36 }}>
                  <Text>{t('executorBlock.refferAs')}</Text>
                </View>

                {subExecutor && (
                  <>
                    <View style={styles.row} wrap={false}>
                      <View style={styles.listNumber}>
                        <Text>{itemCounters.others}.3</Text>
                      </View>
                      <Text>{t('executorBlock.subExecutorText')}</Text>
                    </View>

                    <View style={{ paddingLeft: 36 }}>
                      <Text>
                        {withPlaceholder(getUppercaseName(subExecutor), 'testatorFullName')},{' '}
                        {t('testatorBlock.dateOfBirth')}{' '}
                        {withPlaceholder(
                          subExecutor.birthDate && dates.formatLong(subExecutor.birthDate),
                          'date',
                        )}
                        , {t('testatorBlock.gender')}{' '}
                        {withPlaceholder(
                          subExecutor.gender ? t(`genders.${subExecutor.gender}`) : undefined,
                          'gender',
                        )}
                        .
                      </Text>
                    </View>

                    <View style={styles.row} wrap={false}>
                      <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                        <Text>{itemCounters.others}.3.1</Text>
                      </View>
                      <View style={{ ...styles.beneficiaryInfoRow, paddingRight: 72 }}>
                        <Text>
                          {t('citizenship.primary')}{' '}
                          {withPlaceholder(
                            getCountryName(subExecutor.primaryCitizenship?.country),
                            'country',
                          )}
                          ,{' '}
                          {withPlaceholder(
                            getDocumentCategory(subExecutor.primaryCitizenship?.documentType),
                            'docType',
                          )}
                          : {t('citizenship.number')}{' '}
                          {withPlaceholder(subExecutor.primaryCitizenship?.number, 'number')},{' '}
                          {t('citizenship.dateOfIssue')}{' '}
                          {withPlaceholder(
                            subExecutor.primaryCitizenship?.issueDate &&
                              dates.formatLong(subExecutor.primaryCitizenship?.issueDate),
                            'date',
                          )}
                          .
                        </Text>
                      </View>
                    </View>

                    {subExecutor.otherCitizenships?.length && (
                      <View style={styles.row} wrap={false}>
                        <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                          <Text>
                            {itemCounters.others}.3.{++itemCounters.subExecutor}
                          </Text>
                        </View>
                        <View style={styles.beneficiaryInfoRow}>
                          <Text>
                            {t('citizenship.other')}:{' '}
                            {subExecutor.otherCitizenships?.map((citzenship, i, arr) => (
                              <View key={i}>
                                <Text>
                                  {withPlaceholder(getCountryName(citzenship?.country), 'country')},{' '}
                                  {withPlaceholder(
                                    getDocumentCategory(citzenship?.documentType),
                                    'docType',
                                  )}{' '}
                                  {t('citizenship.number')}{' '}
                                  {withPlaceholder(citzenship?.number, 'number')},{' '}
                                  {t('citizenship.dateOfIssue')}{' '}
                                  {withPlaceholder(
                                    citzenship?.issueDate &&
                                      dates.formatLong(citzenship?.issueDate),
                                    'date',
                                  )}
                                  {i === arr.length - 1 ? '.' : '; '}
                                </Text>
                              </View>
                            ))}
                          </Text>
                        </View>
                      </View>
                    )}

                    {subExecutor.primaryResidence && (
                      <View style={styles.row} wrap={false}>
                        <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                          <Text>
                            {itemCounters.others}.3.{++itemCounters.subExecutor}
                          </Text>
                        </View>
                        <View style={styles.beneficiaryInfoRow}>
                          <Text>
                            {t('residence.primary')}:{' '}
                            {withPlaceholder(
                              getResidenceAddress(subExecutor.primaryResidence),
                              'address',
                            )}
                            {subExecutor.primaryResidence?.tax && (
                              <>
                                , {t('residence.taxId')}: {subExecutor.primaryResidence?.tax}
                              </>
                            )}
                            .
                          </Text>
                        </View>
                      </View>
                    )}

                    {subExecutor.otherResidences?.length && (
                      <View style={styles.row} wrap={false}>
                        <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                          <Text>
                            {itemCounters.others}.3.{++itemCounters.subExecutor}
                          </Text>
                        </View>
                        <View style={styles.beneficiaryInfoRow}>
                          <Text>
                            {t('residence.additional')}:{' '}
                            {subExecutor.otherResidences?.map((residence, i, arr) => (
                              <View key={i}>
                                <Text>
                                  {withPlaceholder(getResidenceAddress(residence), 'address')}
                                  {residence?.tax && (
                                    <>
                                      , {t('residence.taxId')}: {residence?.tax}
                                    </>
                                  )}
                                  {i === arr.length - 1 ? '.' : '; '}
                                </Text>
                              </View>
                            ))}{' '}
                          </Text>
                        </View>
                      </View>
                    )}

                    {(subExecutor.phoneNumber || subExecutor.email) && (
                      <View style={styles.row} wrap={false}>
                        <View style={{ ...styles.listNumber, width: 80, paddingLeft: 36 }}>
                          <Text>
                            {itemCounters.others}.3.{++itemCounters.subExecutor}
                          </Text>
                        </View>
                        <View style={styles.beneficiaryInfoRow}>
                          <Text>
                            {t('contacts.details')}:{' '}
                            {subExecutor.phoneNumber
                              ? `${subExecutor.phoneCode}${subExecutor.phoneNumber}, `
                              : ' '}
                            {subExecutor.email || ''}.
                          </Text>
                        </View>
                      </View>
                    )}

                    <View style={{ paddingLeft: 36 }}>
                      <Text>{t('executorBlock.refferAsSub')}</Text>
                    </View>
                  </>
                )}

                <View style={styles.row} wrap={false}>
                  <View style={styles.listNumber}>
                    <Text>
                      {itemCounters.others}.{subExecutor ? 4 : 3}
                    </Text>
                  </View>
                  <Text>{t('executorBlock.subPowers')}</Text>
                </View>
              </View>
            )}

            <Text style={styles.blockTitle}>
              {++itemCounters.others}. {t('attestation.title')}
            </Text>
            {shariaLaws && (
              <View style={styles.row} wrap={false}>
                <View style={styles.listNumber}>
                  <Text>
                    {itemCounters.others}.{++itemCounters.attestation}
                  </Text>
                </View>
                <Text>{t('attestation.sharia')}</Text>
              </View>
            )}

            <View style={styles.row} wrap={false}>
              <View style={styles.listNumber}>
                <Text>
                  {itemCounters.others}.{++itemCounters.attestation}
                </Text>
              </View>
              <Text>{t('attestation.legalAge')}</Text>
            </View>

            <View style={styles.row} wrap={false}>
              <View style={styles.listNumber}>
                <Text>
                  {itemCounters.others}.{++itemCounters.attestation}
                </Text>
              </View>
              <Text>{t('attestation.english')}</Text>
            </View>

            <View wrap={false} style={{ flexDirection: 'column', gap: 8, width: 604 }}>
              <View style={styles.row}>
                <View style={styles.listNumber}>
                  <Text>
                    {itemCounters.others}.{++itemCounters.attestation}
                  </Text>
                </View>
                <Text>{t('attestation.wishes')}</Text>
              </View>

              <View>
                <Text>
                  {t('attestation.place')}{' '}
                  <Text>{withPlaceholder(executionPlace, 'placeOfExecution')}</Text>
                </Text>
              </View>

              <View>
                <Text>
                  {t('attestation.date')}{' '}
                  {date ? (
                    <Text>{dates.formatLong(date)}</Text>
                  ) : (
                    <Text style={styles.placeholder}>{t('placeholder.todayDate')}</Text>
                  )}
                </Text>
              </View>

              <View>
                <View style={styles.line}></View>
                <Text>{t('attestation.signature')}</Text>
              </View>
            </View>

            <Text style={styles.blockTitle}>
              {++itemCounters.others}. {t('witnessStatementBlock.title')}
            </Text>
            <View>
              <Text>
                {t('witnessStatementBlock.noInterest')}{' '}
                {withPlaceholder(testatorFullName, 'fullName')}:
              </Text>
            </View>
            <View style={{ marginTop: 4, gap: 4 }}>
              <View style={styles.listItem} wrap={false}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.listText}>{t('witnessStatementBlock.invited')}</Text>
              </View>
              <View style={styles.listItem} wrap={false}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.listText}>{t('witnessStatementBlock.signed')}</Text>
              </View>
              <View style={styles.listItem} wrap={false}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.listText}>{t('witnessStatementBlock.aware')}</Text>
              </View>
              <View style={styles.listItem} wrap={false}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.listText}>{t('witnessStatementBlock.legallAge')}</Text>
              </View>
              <View style={styles.listItem} wrap={false}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.listText}>{t('witnessStatementBlock.influence')}</Text>
              </View>
            </View>

            {witnesses.map((item, i) => (
              <View key={i} style={{ gap: 6 }}>
                <Text>
                  {t('witness')} {i + 1}: {getUppercaseName(item)}
                </Text>
                <Text>{t('witnessStatementBlock.signature')}</Text>
                <Text>{t('witnessStatementBlock.dateOfSignature')}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.footer} fixed>
          <View style={styles.footerColumns}>
            <View style={styles.footerColumn}>
              <Text>{t('testator')}:</Text>
            </View>
            <View style={styles.footerColumn}>
              <Text>{t('witness')} 1:</Text>
              <Text>{t('witness')} 2:</Text>
              <Text>{t('witness')} 3:</Text>
            </View>
          </View>
          <View fixed>
            <Text
              style={{ marginTop: 6 }}
              render={({ pageNumber, totalPages }) => {
                return t('page', {
                  page: pageNumber - 1,
                  maxPages: totalPages - 1,
                });
              }}
            />
          </View>
          <View fixed style={styles.poweredByText}>
            <Text>{t('poweredBy')}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
